// フロントエンドのエントリポイント。vanilla TS + MapLibre GL JS。
// フィルタの計算 (filters.ts)、URL 往復 (state.ts)、色 (colorScale.ts)、順位 (rank.ts)、
// 検索 (search.ts)、詳細テンプレート (ssr.ts) はすべて純粋関数として外に置き、
// ここでは UI 状態を 1 つ持って DOM に写す仕事だけをする。
import type { Manifest, Muni } from "../../schema/muni";
import { colorFor, gradientCss, metricDomain, NULL_COLOR } from "./colorScale";
import { FAVORITES_MAX, loadFavorites, saveFavorites, toggleFavorite } from "./favorites";
import {
  activeConditionKeys,
  applyFilters,
  clearConditions,
  countVerdicts,
  DEFAULT_FILTER_STATE,
  filterAndRank,
  relaxOptions,
  type ConditionKey,
  type FilterState,
  type HubKey,
} from "./filters";
import {
  choiceLabel,
  choiceShort,
  FILTER_CHOICES,
  formatSelectNumber,
  parseSelectNumber,
  TSUNAMI_INLAND_VALUE,
  tsunamiSelectToState,
  tsunamiStateToSelectValue,
  type Choice,
  type FilterChoiceField,
} from "./filterChoices";
import {
  DASH,
  DEFAULT_LANG,
  formatNumber,
  LANG_NAMES,
  LANG_PARAM,
  langFromParams,
  otherLang,
  resolveLang,
  t,
  withLang,
} from "./i18n";
import {
  ICON_ARROW_LEFT,
  ICON_CLOSE,
  ICON_EXTERNAL,
  ICON_LOGO,
  ICON_MOON,
  ICON_SEARCH,
  ICON_SHARE,
  ICON_STAR,
  ICON_STAR_FILLED,
  ICON_SUN,
} from "./icons";
import { getMetrics, isMetricKey, METRIC_ORDER, type MetricKey } from "./metrics";
import { muniLabel, prefLabel } from "./prefNames";
import { applyPreset, getPresets, isPresetActive } from "./presets";
import { buildRankIndex, percentileBelow, rankLabelShort, type RankIndex } from "./rank";
import { adSlot, escapeHtml, renderMuniDetailBody } from "./ssr";
import { decodeFilterState, encodeFilterState } from "./state";
import { $, closestAction } from "./ui/dom";
import { createMapView, HAZARD_LAYERS, LIST_CLICK_MIN_ZOOM, SELECT_ZOOM } from "./ui/mapView";
import { createSearchBox } from "./ui/searchBox";
import { createSheet, SHEET_PEEK_HEIGHT, type SheetState } from "./ui/sheet";
import { applyHome } from "./ui/home";
import { applyStaticI18n } from "./ui/staticI18n";
import { applyThemePref, loadThemePref, resolveTheme, saveThemePref, type ThemePref } from "./ui/theme";
import { zeroResultHtml } from "./ui/zeroResult";
import { createToast } from "./ui/toast";

declare global {
  interface Window {
    __ANJU_ENV__?: { gaMeasurementId: string; adsenseClient: string };
    gtag?: (...args: unknown[]) => void;
  }
}

const ENV = window.__ANJU_ENV__ ?? { gaMeasurementId: "", adsenseClient: "" };

// 表示言語は URL だけで決まる（Worker が SSR に使うのと同じ規則）。?lang= が無いときの
// 既定は Worker と揃うよう navigator の言語設定から引く。1 ページの中では変わらないので、
// 文言・指標・プリセットはここで 1 度だけ解決する。
const LANG_QUERY = new URLSearchParams(location.search);
const LANG = resolveLang(LANG_QUERY, navigator.languages?.join(",") ?? navigator.language);
// URL で言語を指定して来たか。指定して来たなら、絞り込みで URL を書き換えた後も lang= を残す
// （残さないと、ブラウザの言語設定が優先されて選んだ言語に戻れない）。
const EXPLICIT_LANG = langFromParams(LANG_QUERY) !== null;
const M = t(LANG);
const METRICS = getMetrics(LANG);
const PRESETS = getPresets(LANG);

function track(event: string, params: Record<string, string | number | boolean>): void {
  if (typeof window.gtag === "function") window.gtag("event", event, params);
}

/** GA4 の地域別集計用。pref は JIS 5 桁コードの先頭 2 桁（都道府県コード）。 */
function muniParams(code: string): { code: string; pref: string } {
  return { code, pref: code.slice(0, 2) };
}

const MOBILE_QUERY = "(max-width: 959px)";
// style.css の --topbar-h / --panel-*-w / --gutter と一致させる。浮いたパネルの裏に日本が隠れないための余白。
const MAP_PADDING_DESKTOP = { top: 72, right: 412, bottom: 16, left: 392 };
const MAP_PADDING_MOBILE = { top: 64, right: 12, bottom: SHEET_PEEK_HEIGHT + 12, left: 12 };
const DRAWER_TRANSITION_MS = 260;
const COUNT_ANIM_MS = 320;
const SKELETON_COUNT = 8;
/** hazard pip を 3 段階に分ける全国分位。 */
const PIP_OK_BELOW = 0.33;
const PIP_WARN_BELOW = 0.66;

const PIP_METRICS: MetricKey[] = ["flood", "tsunami", "sediment", "quake"];

// フィルタ行の定義。行の並び・ラベル・どの FilterState フィールドを触るかをこの 1 表に集める。
interface FilterRow {
  field: FilterChoiceField;
  label: string;
  /** 「指定なし」以外に先頭へ差し込む選択肢（津波の inland だけが該当）。 */
  extraFirst?: { value: string; short: string; label: string };
  /** この指標の値が全件 null な data/munis.json では、行ごと出さない。 */
  requires?: MetricKey;
}

const FILTER_ROWS: FilterRow[] = [
  { field: "floodMax", label: M.tileFlood, requires: "flood" },
  {
    field: "tsunamiMax",
    label: M.tileTsunami,
    extraFirst: { value: TSUNAMI_INLAND_VALUE, short: M.tsunamiInlandShort, label: M.tsunamiInlandLabel },
    requires: "tsunami",
  },
  { field: "sedimentMax", label: M.tileSediment, requires: "sediment" },
  { field: "quakeMax", label: M.tileQuake, requires: "quake" },
  { field: "landPriceMax", label: M.landPriceHeading, requires: "landPrice" },
  { field: "populationMin", label: M.filterPopulation, requires: "population" },
  { field: "tempJanMin", label: M.filterTempJan, requires: "tempJan" },
  { field: "tempAugMax", label: M.filterTempAug, requires: "tempAug" },
];

const HUBS: { key: HubKey; label: string }[] = [
  { key: "tokyo", label: M.hubTokyo },
  { key: "osaka", label: M.hubOsaka },
  { key: "nagoya", label: M.hubNagoya },
];

interface UiState {
  filter: FilterState;
  selected: string | null;
  hovered: string | null;
  favorites: string[];
  theme: ThemePref;
  sheet: SheetState;
  sheetTab: "results" | "filters";
  hazard: Set<string>;
}

function prefersReducedMotion(): boolean {
  return window.matchMedia("(prefers-reduced-motion: reduce)").matches;
}

function pill(field: string, value: string, text: string, title: string, extraClass = ""): string {
  return `<button type="button" class="pill${extraClass ? ` ${extraClass}` : ""}" data-field="${escapeHtml(field)}" data-value="${escapeHtml(value)}" title="${escapeHtml(title)}" aria-pressed="false">${escapeHtml(text)}</button>`;
}

/** フィルタ 1 行分（ラベル + pill 群）。「指定なし」を必ず先頭に置く。 */
function filterRowHtml(row: FilterRow, state: FilterState): string {
  const id = `lbl-${row.field}`;
  const choices: readonly Choice[] = FILTER_CHOICES[row.field];
  const field = row.field === "tsunamiMax" ? "tsunami" : row.field;

  const parts = [pill(field, "", M.anyValue, M.anyValueFor(row.label))];
  if (row.extraFirst) {
    parts.push(pill(field, row.extraFirst.value, row.extraFirst.short, row.extraFirst.label));
  }
  for (const choice of choices) {
    parts.push(
      pill(
        field,
        formatSelectNumber(choice.value),
        choiceShort(row.field, choice.value, LANG),
        `${row.label} ${choiceLabel(row.field, choice.value, LANG, choice.note)}`,
      ),
    );
  }

  // URL 由来の選択肢外の値を黙って落とさず、末尾に 1 つだけ足す
  const current = row.field === "tsunamiMax" ? state.tsunamiMax : state[row.field];
  if (
    typeof current === "number" &&
    !choices.some((c) => c.value === current) &&
    !(row.field === "tsunamiMax" && state.tsunamiInlandOnly)
  ) {
    parts.push(
      pill(
        field,
        formatSelectNumber(current),
        choiceShort(row.field, current, LANG),
        choiceLabel(row.field, current, LANG),
      ),
    );
  }

  return `<div class="frow">
      <p class="frow-label" id="${id}">${escapeHtml(row.label)}</p>
      <div class="pills" role="radiogroup" aria-labelledby="${id}">${parts.join("")}</div>
    </div>`;
}

/**
 * data/munis.json の世代によっては、指標のフィールドが 1 件も入っていないことがある
 * （出典を足した直後で pipeline を回し直していない、など）。値が 1 つも無い指標を
 * 出しても地図が灰色になるだけなので、選べる指標・絞り込み行から外す。
 * 今選ばれている指標だけは、URL から復元された選択を黙って消さないために残す。
 */
function filterFormHtml(prefs: string[], state: FilterState, available: Set<MetricKey>): string {
  const shown = (key: MetricKey): boolean => available.has(key) || state.metric === key;

  const presetPills = PRESETS.map((p) =>
    (p.requires ?? []).every((key) => available.has(key))
      ? pill("preset", p.id, p.label, p.description, "pill-preset")
      : "",
  ).join("");

  const metricPills = METRIC_ORDER.filter(shown)
    .map((key) => pill("metric", key, METRICS[key].short, METRICS[key].label))
    .join("");

  const prefOptions = [`<option value="">${escapeHtml(M.allPrefectures)}</option>`]
    .concat(
      prefs.map((p) => `<option value="${escapeHtml(p)}">${escapeHtml(prefLabel(p, LANG))}</option>`),
    )
    .join("");

  const hubPills = HUBS.map((h) => pill("hub", h.key, h.label, M.hubTitle(h.label))).join("");
  const distPills = [pill("hubDistMax", "", M.anyValue, M.anyValueFor(M.hubDistance))]
    .concat(
      FILTER_CHOICES.hubDistMax.map((c) =>
        pill(
          "hubDistMax",
          formatSelectNumber(c.value),
          choiceShort("hubDistMax", c.value, LANG),
          M.hubDistTitle(choiceLabel("hubDistMax", c.value, LANG)),
        ),
      ),
    )
    .join("");

  const hazardChips = HAZARD_LAYERS.map((h) => {
    const label = M.hazardLayer[h.id] ?? h.id;
    return `<button type="button" class="pill pill-hazard" data-field="hazard" data-value="${escapeHtml(h.id)}" aria-pressed="false" title="${escapeHtml(M.hazardChipTitle(label))}"><span class="hz-dot" data-hz="${escapeHtml(h.id)}"></span>${escapeHtml(label)}</button>`;
  }).join("");

  return `<section class="fsec">
      <h2 class="fsec-title">${escapeHtml(M.quickStart)}</h2>
      <div class="pills pills-wrap" role="group" aria-label="${escapeHtml(M.quickStart)}">${presetPills}</div>
    </section>

    <section class="fsec">
      <h2 class="fsec-title">${escapeHtml(M.mapColorAndSort)}</h2>
      <div class="pills pills-grid" role="radiogroup" aria-label="${escapeHtml(M.metrics)}">${metricPills}</div>
      <p class="fsec-hint" id="metric-hint"></p>
    </section>

    <section class="fsec">
      <h2 class="fsec-title">${escapeHtml(M.filters)}
        <span class="badge" id="active-count">${escapeHtml(M.activeCount(0))}</span>
        <button type="button" class="text-btn" id="f-reset">${escapeHtml(M.reset)}</button>
      </h2>
      <div class="frow">
        <label class="frow-label" for="f-pref">${escapeHtml(M.prefecture)}</label>
        <select id="f-pref" class="select" data-field="pref">${prefOptions}</select>
      </div>
      ${FILTER_ROWS.filter((r) => r.requires === undefined || shown(r.requires))
        .map((r) => filterRowHtml(r, state))
        .join("")}
      <div class="frow">
        <p class="frow-label" id="lbl-hub">${escapeHtml(M.hubDistance)}</p>
        <div class="pills pills-seg" role="radiogroup" aria-label="${escapeHtml(M.hubStations)}">${hubPills}</div>
        <div class="pills" role="radiogroup" aria-labelledby="lbl-hub">${distPills}</div>
      </div>
    </section>

    <section class="fsec">
      <h2 class="fsec-title">${escapeHtml(M.hazardOverlay)}</h2>
      <div class="pills pills-wrap" role="group" aria-label="${escapeHtml(M.hazardOverlay)}">${hazardChips}</div>
      <p class="fsec-note">${escapeHtml(M.hazardOverlayNote)}</p>
    </section>

    <div id="ad-slot-sidebar" class="ad-wrap">${adSlot(ENV.adsenseClient, "sidebar")}</div>`;
}

async function loadJson<T>(path: string): Promise<T> {
  const res = await fetch(path);
  if (!res.ok) throw new Error(`${path} の取得に失敗しました (${res.status})`);
  return (await res.json()) as T;
}

function skeletonHtml(): string {
  return Array.from({ length: SKELETON_COUNT }, () => `<li class="card card-skeleton" aria-hidden="true"></li>`).join("");
}

/** 件数の増減を滑らかに見せる。reduced-motion では即時に確定させる。 */
function animateCount(el: HTMLElement, from: number, to: number, format: (n: number) => string): void {
  if (from === to || prefersReducedMotion()) {
    el.textContent = format(to);
    return;
  }
  const start = performance.now();
  const step = (now: number): void => {
    const progress = Math.min(1, (now - start) / COUNT_ANIM_MS);
    const eased = 1 - (1 - progress) ** 3;
    el.textContent = format(Math.round(from + (to - from) * eased));
    if (progress < 1) requestAnimationFrame(step);
  };
  requestAnimationFrame(step);
}

function countActiveFilters(state: FilterState): number {
  return activeConditionKeys(state).length;
}

async function main(): Promise<void> {
  document.documentElement.lang = LANG;
  applyStaticI18n(LANG, EXPLICIT_LANG);

  const listEl = $<HTMLOListElement>("#muni-list");
  listEl.innerHTML = skeletonHtml();

  $<HTMLElement>("#brand-mark").innerHTML = ICON_LOGO;
  $<HTMLElement>("#search-icon").innerHTML = ICON_SEARCH;
  $<HTMLElement>("#fav-button-icon").innerHTML = ICON_STAR;
  $<HTMLElement>("#drawer-back").innerHTML = ICON_ARROW_LEFT;
  $<HTMLElement>("#drawer-close").innerHTML = ICON_CLOSE;
  $<HTMLElement>("#drawer-share").innerHTML = ICON_SHARE;
  $<HTMLElement>("#drawer-open").innerHTML = ICON_EXTERNAL;

  const [munis, manifest] = await Promise.all([
    loadJson<Muni[]>("/data/munis.json"),
    loadJson<Manifest>("/data/manifest.json"),
  ]);

  const byCode = new Map(munis.map((m) => [m.code, m]));
  const rankIndex: RankIndex = buildRankIndex(munis);
  // 並び順は表示ラベル基準。英語では Aichi, Akita, ... とアルファベット順になる。
  const prefs = [...new Set(munis.map((m) => m.pref))].sort((a, b) =>
    prefLabel(a, LANG).localeCompare(prefLabel(b, LANG), LANG),
  );

  const params = new URLSearchParams(location.search);
  const selParam = params.get("sel");
  const ui: UiState = {
    filter: decodeFilterState(params),
    selected: selParam !== null && byCode.has(selParam) ? selParam : null,
    hovered: null,
    favorites: loadFavorites(localStorage),
    theme: loadThemePref(localStorage),
    sheet: "peek",
    sheetTab: "results",
    hazard: new Set(),
  };

  const form = $<HTMLFormElement>("#filter-form");
  const filterPanel = $<HTMLElement>("#filter-panel");
  const resultsPanel = $<HTMLElement>("#results-panel");
  const resultsBody = $<HTMLElement>("#results-body");
  const sheetRoot = $<HTMLElement>("#sheet");
  const sheetResults = $<HTMLElement>("#sheet-results");
  const sheetFilters = $<HTMLElement>("#sheet-filters");
  const resultCountEl = $<HTMLElement>("#result-count");
  const resultLiveEl = $<HTMLElement>("#result-live");
  const resultSortEl = $<HTMLElement>("#result-sort");
  const resultUnmeasuredEl = $<HTMLElement>("#result-unmeasured");
  const sheetCountEl = $<HTMLElement>("#sheet-count");
  const sheetActiveEl = $<HTMLElement>("#sheet-active");
  const drawer = $<HTMLElement>("#detail-drawer");
  const drawerBody = $<HTMLElement>("#drawer-body");
  const drawerFav = $<HTMLButtonElement>("#drawer-fav");
  const drawerOpen = $<HTMLAnchorElement>("#drawer-open");
  const themeToggle = $<HTMLButtonElement>("#theme-toggle");
  const favCountEl = $<HTMLElement>("#fav-count");
  const favListEl = $<HTMLUListElement>("#fav-list");
  const favPopover = $<HTMLElement>("#fav-popover");
  const legendEl = $<HTMLElement>("#legend");
  const searchInput = $<HTMLInputElement>("#search-input");
  const langSwitch = $<HTMLAnchorElement>("#lang-switch");
  const showToast = createToast($<HTMLElement>("#toast"));

  // rankIndex は指標ごとに非 null 値だけを持つので、そのまま「値のある指標」の判定に使える。
  const availableMetrics = new Set(METRIC_ORDER.filter((key) => rankIndex[key].length > 0));
  form.innerHTML = filterFormHtml(prefs, ui.filter, availableMetrics);
  const prefSelect = $<HTMLSelectElement>("#f-pref", form);
  const metricHintEl = $<HTMLElement>("#metric-hint", form);
  const activeCountEl = $<HTMLElement>("#active-count", form);

  applyThemePref(ui.theme);
  renderThemeButton();
  drawerFav.innerHTML = ICON_STAR;

  function metricValueText(muni: Muni): string {
    const def = METRICS[ui.filter.metric];
    const value = def.get(muni);
    return value === null ? DASH : def.format(value);
  }

  const mapView = createMapView({
    container: "map",
    padding: () => (window.matchMedia(MOBILE_QUERY).matches ? MAP_PADDING_MOBILE : MAP_PADDING_DESKTOP),
    onSelect: (code) => selectMuni(code, { fly: false }),
    onHover: (code) => {
      ui.hovered = code;
      paintHover(code);
    },
    describeHover: (code) => {
      const muni = byCode.get(code);
      if (!muni) return "";
      return `<p class="tip-name"><span>${escapeHtml(prefLabel(muni.pref, LANG))}</span>${escapeHtml(muni.name)}</p>
        <p class="tip-value">${escapeHtml(METRICS[ui.filter.metric].label)} <b>${escapeHtml(metricValueText(muni))}</b></p>`;
    },
  });
  mapView.setTheme(resolveTheme(ui.theme));

  const sheet = createSheet({
    root: sheetRoot,
    header: $<HTMLElement>("#sheet-header"),
    handle: $<HTMLElement>("#sheet-handle"),
    onStateChange: (state) => {
      ui.sheet = state;
    },
  });

  createSearchBox({
    input: searchInput,
    list: $<HTMLUListElement>("#search-list"),
    munis,
    lang: LANG,
    describe: metricValueText,
    onSelect: (muni) => {
      track("search_select", muniParams(muni.code));
      selectMuni(muni.code, { fly: true, zoom: SELECT_ZOOM });
    },
  });

  function syncUrl(): void {
    const next = encodeFilterState(ui.filter);
    if (ui.selected !== null) next.set("sel", ui.selected);
    if (LANG !== DEFAULT_LANG || EXPLICIT_LANG) next.set(LANG_PARAM, LANG);
    const qs = next.toString();
    history.replaceState(null, "", qs ? `?${qs}` : location.pathname);
    syncLangSwitch();
  }

  /**
   * 言語の切り替えはページ遷移で行う（状態はすべて URL にあるので、絞り込みも選択も保たれる）。
   * URL は絞り込みのたびに書き換わるため、リンク先もそのつど作り直す。
   */
  function syncLangSwitch(): void {
    const next = otherLang(LANG);
    const url = new URL(location.href);
    // 切り替え先は日本語でも lang= を明示する（Accept-Language に上書きされないため）。
    url.searchParams.set(LANG_PARAM, next);
    langSwitch.href = `${url.pathname}${url.search}`;
    langSwitch.textContent = LANG_NAMES[next];
    langSwitch.setAttribute("hreflang", next);
    langSwitch.setAttribute("lang", next);
    langSwitch.setAttribute("aria-label", M.switchLanguage);
  }

  function patchFilter(patch: Partial<FilterState>, changedKey: string): void {
    ui.filter = { ...ui.filter, ...patch };
    syncUrl();
    render();
    track("filter_change", { key: changedKey });
  }

  // ---- 描画 ------------------------------------------------------------

  function paintPressed(field: string, value: string): void {
    for (const btn of form.querySelectorAll<HTMLButtonElement>(`[data-field="${field}"]`)) {
      btn.setAttribute("aria-pressed", String(btn.dataset.value === value));
    }
  }

  function renderFilters(): void {
    const s = ui.filter;
    paintPressed("metric", s.metric);
    paintPressed("floodMax", formatSelectNumber(s.floodMax));
    paintPressed("tsunami", tsunamiStateToSelectValue(s.tsunamiMax, s.tsunamiInlandOnly));
    paintPressed("sedimentMax", formatSelectNumber(s.sedimentMax));
    paintPressed("quakeMax", formatSelectNumber(s.quakeMax));
    paintPressed("landPriceMax", formatSelectNumber(s.landPriceMax));
    paintPressed("populationMin", formatSelectNumber(s.populationMin));
    paintPressed("tempJanMin", formatSelectNumber(s.tempJanMin));
    paintPressed("tempAugMax", formatSelectNumber(s.tempAugMax));
    paintPressed("hub", s.hub);
    paintPressed("hubDistMax", formatSelectNumber(s.hubDistMax));

    for (const btn of form.querySelectorAll<HTMLButtonElement>('[data-field="preset"]')) {
      const preset = PRESETS.find((p) => p.id === btn.dataset.value);
      btn.setAttribute("aria-pressed", String(preset !== undefined && isPresetActive(s, preset)));
    }
    for (const btn of form.querySelectorAll<HTMLButtonElement>('[data-field="hazard"]')) {
      btn.setAttribute("aria-pressed", String(ui.hazard.has(btn.dataset.value ?? "")));
    }

    if (prefSelect.value !== (s.pref ?? "")) prefSelect.value = s.pref ?? "";
    metricHintEl.textContent = METRICS[s.metric].hint;

    const active = countActiveFilters(s);
    activeCountEl.textContent = M.activeCount(active);
    activeCountEl.hidden = active === 0;
    sheetActiveEl.textContent = String(active);
    sheetActiveEl.hidden = active === 0;
  }

  function renderLegend(domain: [number, number]): void {
    const def = METRICS[ui.filter.metric];
    $<HTMLElement>("#legend-title", legendEl).textContent = def.label;
    $<HTMLElement>("#legend-bar", legendEl).style.background = gradientCss(def.scale);
    $<HTMLElement>("#legend-lo", legendEl).textContent = def.format(domain[0]);
    $<HTMLElement>("#legend-hi", legendEl).textContent = def.format(domain[1]);
    $<HTMLElement>("#legend-hint", legendEl).textContent = def.hint;
  }

  /** 4 つの災害指標を、全国分位の 3 段階に色分けした点で並べる。 */
  function pipsHtml(muni: Muni): string {
    const pips: string[] = [];
    const summary: string[] = [];
    for (const key of PIP_METRICS) {
      const def = METRICS[key];
      const value = def.get(muni);
      if (value === null) {
        const missing = M.pipNoData(def.short);
        summary.push(missing);
        pips.push(`<i class="pip pip-none" title="${escapeHtml(missing)}"></i>`);
        continue;
      }
      const p = percentileBelow(rankIndex, key, value);
      const level = p < PIP_OK_BELOW ? "ok" : p < PIP_WARN_BELOW ? "warn" : "danger";
      const text = M.pipSummary(def.short, def.format(value), rankLabelShort(rankIndex, key, value, LANG));
      summary.push(text);
      pips.push(`<i class="pip pip-${level}" title="${escapeHtml(text)}"></i>`);
    }
    return `<span class="pips" role="img" aria-label="${escapeHtml(summary.join(M.listSep))}">${pips.join("")}</span>`;
  }

  function cardHtml(muni: Muni, index: number, domain: [number, number]): string {
    const def = METRICS[ui.filter.metric];
    const value = def.get(muni);
    const color = colorFor(value, domain, def.scale);
    const width =
      value === null
        ? 0
        : Math.min(100, Math.max(2, ((value - domain[0]) / (domain[1] - domain[0])) * 100));
    const isFav = ui.favorites.includes(muni.code);

    return `<li class="card${ui.selected === muni.code ? " is-selected" : ""}" data-code="${escapeHtml(muni.code)}">
        <button type="button" class="card-main" data-code="${escapeHtml(muni.code)}">
          <span class="card-rank">${index + 1}</span>
          <span class="card-id">
            <span class="card-pref">${escapeHtml(prefLabel(muni.pref, LANG))}</span>
            <span class="card-name">${escapeHtml(muni.name)}</span>
          </span>
          <span class="card-num">${escapeHtml(value === null ? DASH : def.format(value))}</span>
          <span class="card-bar"><i style="width: ${width.toFixed(1)}%; background: ${escapeHtml(color)}"></i></span>
          ${pipsHtml(muni)}
        </button>
        <button type="button" class="star-btn" data-fav="${escapeHtml(muni.code)}" aria-pressed="${isFav}" aria-label="${escapeHtml(M.favoriteToggle(muniLabel(muni.pref, muni.name, LANG), isFav))}">${isFav ? ICON_STAR_FILLED : ICON_STAR}</button>
      </li>`;
  }

  let lastCount = 0;

  /**
   * 絞り込んだ指標に値が無い市区町村の件数と、それを外すトグル。値が無いだけの
   * 市区町村が黙って一覧に混ざると、条件と結果が食い違って見える。
   * 戻り値は読み上げにも足す文（出すものが無ければ空文字）。
   */
  function renderUnmeasured(): string {
    const { unmeasured } = countVerdicts(munis, ui.filter);
    const excluded = ui.filter.excludeUnmeasured;

    // 除外中は 0 件でもトグルを残す。消すと元に戻す手段が無くなる。
    if (!excluded && unmeasured === 0) {
      resultUnmeasuredEl.hidden = true;
      resultUnmeasuredEl.innerHTML = "";
      return "";
    }

    const note = excluded ? M.unmeasuredExcluded(unmeasured) : M.unmeasuredNote(unmeasured);
    const action = excluded ? M.unmeasuredInclude : M.unmeasuredExclude;
    resultUnmeasuredEl.hidden = false;
    resultUnmeasuredEl.innerHTML = `${escapeHtml(note)}<button type="button" class="text-btn" id="unmeasured-toggle" aria-pressed="${excluded}">${escapeHtml(action)}</button>`;
    return note;
  }

  function renderResults(filtered: Muni[], ranked: Muni[], domain: [number, number]): void {
    const def = METRICS[ui.filter.metric];
    animateCount(resultCountEl, lastCount, filtered.length, M.resultCount);
    lastCount = filtered.length;
    const sortText =
      filtered.length > ranked.length
        ? M.sortedByTop(def.label, ranked.length, def.betterWord)
        : M.sortedBy(def.label, def.betterWord);
    resultSortEl.textContent = sortText;
    const unmeasuredNote = renderUnmeasured();
    const summary = M.resultSummary(M.resultCount(filtered.length), sortText);
    // 区切り文字を書き分けたくないので、内訳の連結にも resultSummary を使う。
    resultLiveEl.textContent = unmeasuredNote === "" ? summary : M.resultSummary(summary, unmeasuredNote);
    sheetCountEl.textContent = formatNumber(filtered.length, LANG);

    // 0 件のときだけ、結果一覧の先頭に逃げ道を差し込む（一覧はデスクトップのパネルと
    // ボトムシートで同じ DOM を使い回すので、置き場所はここ 1 箇所で足りる）。
    const zeroHtml = filtered.length === 0 ? zeroResultHtml(relaxOptions(munis, ui.filter), ui.filter, LANG) : "";
    listEl.innerHTML = zeroHtml + ranked.map((m, i) => cardHtml(m, i, domain)).join("");
    paintHover(ui.hovered);
  }

  function renderFavorites(): void {
    favCountEl.textContent = String(ui.favorites.length);
    const def = METRICS[ui.filter.metric];
    if (ui.favorites.length === 0) {
      favListEl.innerHTML = `<li class="fav-empty">${escapeHtml(M.favoritesEmpty)}</li>`;
      return;
    }
    favListEl.innerHTML = ui.favorites
      .map((code) => {
        const muni = byCode.get(code);
        if (!muni) return "";
        const value = def.get(muni);
        return `<li class="fav-item">
            <button type="button" class="fav-go" data-code="${escapeHtml(code)}">
              <span class="fav-name"><span class="fav-pref">${escapeHtml(prefLabel(muni.pref, LANG))}</span>${escapeHtml(muni.name)}</span>
              <span class="fav-value">${escapeHtml(value === null ? DASH : def.format(value))}</span>
            </button>
            <button type="button" class="fav-remove" data-fav-remove="${escapeHtml(code)}" aria-label="${escapeHtml(M.removeFromFavorites)}">×</button>
          </li>`;
      })
      .join("");
  }

  function buildGeoJson(list: Muni[], domain: [number, number]): GeoJSON.FeatureCollection {
    const def = METRICS[ui.filter.metric];
    return {
      type: "FeatureCollection",
      features: list.map((m) => {
        const value = def.get(m);
        return {
          type: "Feature",
          geometry: { type: "Point", coordinates: m.lonlat },
          properties: {
            code: m.code,
            hasValue: value !== null,
            color: value === null ? NULL_COLOR : colorFor(value, domain, def.scale),
          },
        };
      }),
    };
  }

  // 色の基準は全国分布に固定する。絞り込むたびに同じ値の色が変わると、
  // 「赤い＝リスクが高い」という読み方がフィルタごとに崩れてしまう。
  const metricDomains = new Map<MetricKey, [number, number]>(
    METRIC_ORDER.map((key) => [key, metricDomain(munis, key)]),
  );

  function render(): void {
    const filtered = applyFilters(munis, ui.filter);
    const ranked = filterAndRank(munis, ui.filter);
    const domain = metricDomains.get(ui.filter.metric) ?? [0, 1];

    mapView.setData(buildGeoJson(filtered, domain));
    renderFilters();
    renderLegend(domain);
    renderResults(filtered, ranked, domain);
    renderFavorites();
  }

  function paintHover(code: string | null): void {
    for (const el of listEl.querySelectorAll(".card.is-hover")) el.classList.remove("is-hover");
    if (code === null) return;
    listEl.querySelector(`.card[data-code="${CSS.escape(code)}"]`)?.classList.add("is-hover");
  }

  function paintSelected(): void {
    for (const el of listEl.querySelectorAll(".card.is-selected")) el.classList.remove("is-selected");
    if (ui.selected === null) return;
    listEl.querySelector(`.card[data-code="${CSS.escape(ui.selected)}"]`)?.classList.add("is-selected");
  }

  // ---- 詳細ドロワー -----------------------------------------------------

  function setDrawerOpen(open: boolean): void {
    if (open) {
      drawer.hidden = false;
      requestAnimationFrame(() => drawer.classList.add("is-open"));
    } else {
      drawer.classList.remove("is-open");
      setTimeout(() => {
        if (!drawer.classList.contains("is-open")) drawer.hidden = true;
      }, DRAWER_TRANSITION_MS);
    }
  }

  function selectMuni(code: string, opts: { fly: boolean; zoom?: number }): void {
    const muni = byCode.get(code);
    if (!muni) return;

    ui.selected = code;
    drawerBody.innerHTML =
      renderMuniDetailBody(muni, manifest, rankIndex, LANG) +
      `<section class="ad-below">${adSlot(ENV.adsenseClient, "detail-below")}</section>`;
    // 現住地との比較行は SSR ページと同じ経路で埋める（詳細の見た目は 1 箇所にしか無い）
    applyHome(drawerBody, localStorage, LANG, track);
    drawer.setAttribute("aria-label", M.detailOf(muniLabel(muni.pref, muni.name, LANG)));
    drawerOpen.href = withLang(`/m/${muni.code}`, LANG, EXPLICIT_LANG);
    syncFavButton(code);
    setDrawerOpen(true);

    mapView.setSelected(code, muni.lonlat);
    if (opts.fly) mapView.flyTo(muni.lonlat, opts.zoom ?? LIST_CLICK_MIN_ZOOM);
    paintSelected();
    syncUrl();
    track("municipality_view", muniParams(code));
  }

  function closeDetail(): void {
    ui.selected = null;
    setDrawerOpen(false);
    mapView.setSelected(null, null);
    paintSelected();
    syncUrl();
  }

  function syncFavButton(code: string): void {
    const on = ui.favorites.includes(code);
    drawerFav.setAttribute("aria-pressed", String(on));
    drawerFav.setAttribute("aria-label", on ? M.removeFromFavorites : M.addToFavorites);
    drawerFav.innerHTML = on ? ICON_STAR_FILLED : ICON_STAR;
  }

  function toggleFav(code: string): void {
    const wasFull = ui.favorites.length >= FAVORITES_MAX && !ui.favorites.includes(code);
    ui.favorites = toggleFavorite(ui.favorites, code);
    saveFavorites(localStorage, ui.favorites);
    const on = ui.favorites.includes(code);
    if (ui.selected !== null) syncFavButton(ui.selected);
    renderFavorites();
    for (const btn of listEl.querySelectorAll<HTMLButtonElement>(`[data-fav="${CSS.escape(code)}"]`)) {
      btn.setAttribute("aria-pressed", String(on));
      btn.innerHTML = on ? ICON_STAR_FILLED : ICON_STAR;
    }
    if (wasFull) showToast(M.favoritesFull(FAVORITES_MAX));
    track("favorite_toggle", { ...muniParams(code), on });
  }

  // ---- テーマ -----------------------------------------------------------

  function renderThemeButton(): void {
    const resolved = resolveTheme(ui.theme);
    themeToggle.innerHTML = resolved === "dark" ? ICON_SUN : ICON_MOON;
    themeToggle.setAttribute("aria-label", resolved === "dark" ? M.switchToLight : M.switchToDark);
  }

  themeToggle.addEventListener("click", () => {
    const next: ThemePref = resolveTheme(ui.theme) === "dark" ? "light" : "dark";
    ui.theme = next;
    applyThemePref(next);
    saveThemePref(localStorage, next);
    mapView.setTheme(next);
    renderThemeButton();
  });

  window.matchMedia("(prefers-color-scheme: dark)").addEventListener("change", () => {
    if (ui.theme !== "system") return;
    mapView.setTheme(resolveTheme(ui.theme));
    renderThemeButton();
  });

  // ---- イベント配線 -----------------------------------------------------

  form.addEventListener("click", (ev) => {
    const btn = closestAction(ev.target, "data-field");
    if (!(btn instanceof HTMLButtonElement)) return;
    const field = btn.dataset.field ?? "";
    const value = btn.dataset.value ?? "";

    if (field === "preset") {
      const preset = PRESETS.find((p) => p.id === value);
      if (!preset) return;
      const wasActive = isPresetActive(ui.filter, preset);
      ui.filter = applyPreset(ui.filter, preset);
      syncUrl();
      render();
      track("preset_apply", { id: preset.id, on: !wasActive });
      return;
    }

    if (field === "hazard") {
      const on = !ui.hazard.has(value);
      if (on) ui.hazard.add(value);
      else ui.hazard.delete(value);
      mapView.setHazardVisible(value, on);
      renderFilters();
      track("hazard_layer_toggle", { layer: value, on });
      return;
    }

    if (field === "metric" && isMetricKey(value)) {
      patchFilter({ metric: value }, "metric");
      return;
    }

    if (field === "hub") {
      patchFilter({ hub: value as HubKey }, "hub");
      return;
    }

    if (field === "tsunami") {
      patchFilter(tsunamiSelectToState(value), "tsunami");
      return;
    }

    if (field in FILTER_CHOICES) {
      patchFilter({ [field]: parseSelectNumber(value) }, field);
    }
  });

  form.addEventListener("change", (ev) => {
    if (ev.target !== prefSelect) return;
    patchFilter({ pref: prefSelect.value === "" ? null : prefSelect.value }, "pref");
  });

  $<HTMLButtonElement>("#f-reset", form).addEventListener("click", () => {
    ui.filter = { ...DEFAULT_FILTER_STATE };
    syncUrl();
    render();
    track("filter_change", { key: "reset" });
  });

  // 未計測の除外もフィルタ 1 つとして扱うので、通常の filter_change に乗せる。
  resultUnmeasuredEl.addEventListener("click", (ev) => {
    if (!(ev.target instanceof Element) || ev.target.closest("#unmeasured-toggle") === null) return;
    patchFilter({ excludeUnmeasured: !ui.filter.excludeUnmeasured }, "excludeUnmeasured");
  });

  /** 0 件パネルのボタン。通常のフィルタ変更と同じ経路（URL → 再描画）を通す。 */
  function relaxCondition(key: string): void {
    if (key === "all") {
      const next = clearConditions(ui.filter);
      const count = applyFilters(munis, next).length;
      ui.filter = next;
      syncUrl();
      render();
      track("zero_result_relax", { key, count });
      return;
    }
    const option = relaxOptions(munis, ui.filter).find((o) => o.key === key);
    if (!option) return;
    ui.filter = { ...ui.filter, [option.key]: DEFAULT_FILTER_STATE[option.key as ConditionKey] };
    syncUrl();
    render();
    track("zero_result_relax", { key, count: option.count });
  }

  listEl.addEventListener("click", (ev) => {
    const relax = closestAction(ev.target, "data-relax");
    if (relax) {
      relaxCondition(relax.dataset.relax ?? "");
      return;
    }
    const star = closestAction(ev.target, "data-fav");
    if (star) {
      toggleFav(star.dataset.fav ?? "");
      return;
    }
    const card = closestAction(ev.target, "data-code");
    if (card) selectMuni(card.dataset.code ?? "", { fly: true, zoom: LIST_CLICK_MIN_ZOOM });
  });

  listEl.addEventListener("pointerover", (ev) => {
    const card = closestAction(ev.target, "data-code");
    const code = card?.dataset.code ?? null;
    if (code === ui.hovered) return;
    ui.hovered = code;
    mapView.setHovered(code);
    paintHover(code);
  });

  listEl.addEventListener("pointerleave", () => {
    ui.hovered = null;
    mapView.setHovered(null);
    paintHover(null);
  });

  favListEl.addEventListener("click", (ev) => {
    const remove = closestAction(ev.target, "data-fav-remove");
    if (remove) {
      toggleFav(remove.dataset.favRemove ?? "");
      return;
    }
    const go = closestAction(ev.target, "data-code");
    if (!go) return;
    favPopover.hidePopover();
    selectMuni(go.dataset.code ?? "", { fly: true, zoom: SELECT_ZOOM });
  });

  drawerFav.addEventListener("click", () => {
    if (ui.selected !== null) toggleFav(ui.selected);
  });

  drawerBody.addEventListener("click", (ev) => {
    const link = ev.target instanceof Element ? ev.target.closest<HTMLAnchorElement>("a[data-akiya-target]") : null;
    if (!link) return;
    track("akiya_bank_click", { ...muniParams(ui.selected ?? ""), target: link.dataset.akiyaTarget ?? "" });
  });

  $<HTMLButtonElement>("#drawer-share").addEventListener("click", async () => {
    if (ui.selected === null) return;
    const url = `${location.origin}${withLang(`/m/${ui.selected}`, LANG, EXPLICIT_LANG)}`;
    try {
      await navigator.clipboard.writeText(url);
      showToast(M.linkCopied);
    } catch {
      showToast(M.copyFailed);
    }
    track("share_copy", muniParams(ui.selected ?? ""));
  });

  for (const id of ["#drawer-close", "#drawer-back"]) {
    $<HTMLButtonElement>(id).addEventListener("click", closeDetail);
  }

  document.addEventListener("keydown", (ev) => {
    if (ev.key !== "Escape" || drawer.hidden) return;
    if (document.activeElement === searchInput) return;
    closeDetail();
  });

  // ---- レイアウトの付け替え（DOM は複製せず、置き場所だけを変える） --------

  const mobile = window.matchMedia(MOBILE_QUERY);

  function placePanels(isMobile: boolean): void {
    if (isMobile) {
      if (form.parentElement !== sheetFilters) sheetFilters.append(form);
      if (resultsBody.parentElement !== sheetResults) sheetResults.append(resultsBody);
    } else {
      if (form.parentElement !== filterPanel) filterPanel.append(form);
      if (resultsBody.parentElement !== resultsPanel) resultsPanel.append(resultsBody);
    }
    mapView.resize();
  }

  placePanels(mobile.matches);
  mobile.addEventListener("change", (ev) => placePanels(ev.matches));

  sheetRoot.addEventListener("click", (ev) => {
    const tab = closestAction(ev.target, "data-tab");
    if (!tab) return;
    const next = tab.dataset.tab === "filters" ? "filters" : "results";
    ui.sheetTab = next;
    for (const el of sheetRoot.querySelectorAll<HTMLButtonElement>("[data-tab]")) {
      el.setAttribute("aria-selected", String(el.dataset.tab === next));
    }
    sheetResults.hidden = next !== "results";
    sheetFilters.hidden = next !== "filters";
    if (sheet.getState() === "peek") sheet.setState("half");
  });

  // ---- 起動 -------------------------------------------------------------

  window.addEventListener("popstate", () => {
    const next = new URLSearchParams(location.search);
    ui.filter = decodeFilterState(next);
    const sel = next.get("sel");
    if (sel !== null && byCode.has(sel)) selectMuni(sel, { fly: false });
    else closeDetail();
    render();
  });

  syncLangSwitch();
  render();

  if (ui.selected !== null) selectMuni(ui.selected, { fly: true, zoom: SELECT_ZOOM });
}

main().catch((err) => {
  console.error(err);
  const el = document.querySelector<HTMLElement>("#result-count");
  if (el) el.textContent = M.loadFailed;
  const list = document.querySelector<HTMLElement>("#muni-list");
  if (list) list.innerHTML = "";
});
